import React, { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, Clock, MapPin, Users, Check, X, Shield, CloudRain, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { ExperienceCard } from "../components/ExperienceCard";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import { eur, pricingLabel, formatDate } from "../lib/format";
import * as apiClient from "../api";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { addItem } = useCart();
  const navigate = useNavigate();

  const { data: exp, isLoading } = useQuery({ queryKey: ["exp", slug], queryFn: () => apiClient.getExperience(slug) });
  const { data: availability = [] } = useQuery({ queryKey: ["avail", slug], queryFn: () => apiClient.getAvailability(slug), enabled: !!slug });
  const { data: related = [] } = useQuery({ queryKey: ["related", slug], queryFn: () => apiClient.getRelated(slug), enabled: !!slug });
  const { data: settings } = useQuery({ queryKey: ["publicSettings"], queryFn: apiClient.getPublicSettings });

  const [imgIdx, setImgIdx] = useState(0);
  const [selDate, setSelDate] = useState("");
  const [selTime, setSelTime] = useState("");
  const [participants, setParticipants] = useState(2);

  const daySlots = useMemo(() => {
    const day = availability.find((a) => a.date === selDate);
    return day ? day.slots : [];
  }, [availability, selDate]);

  const selSlot = daySlots.find((s) => s.time === selTime);
  const remaining = selSlot ? selSlot.available : null;
  const maxSelectable = remaining != null ? Math.min(exp?.max_participants ?? 1, remaining) : (exp?.max_participants ?? 1);
  const noSeats = remaining != null && remaining <= 0;
  const overRequested = remaining != null && participants > remaining;

  const pickTime = useCallback((time, available) => {
    setSelTime(time);
    setParticipants((p) => Math.max(1, Math.min(p, available > 0 ? available : 1)));
  }, []);

  const addToCart = useCallback(() => {
    if (!exp) return;
    if (!selDate || !selTime) { toast.error(t("toast.selectdatetime")); return; }
    if (remaining != null && (remaining <= 0 || participants > remaining)) { toast.error(t("toast.availchanged")); return; }
    const st = exp.pricing_type === "per_person" ? exp.price * participants : exp.price;
    addItem({
      experience_id: exp.id, title: exp.title, slug: exp.slug, image: exp.images[0],
      date: selDate, time: selTime, participants: Number(participants),
      price: exp.price, subtotal: st, pricing_type: exp.pricing_type, operator_name: exp.operator_name,
    });
    toast.success(t("toast.added"));
  }, [exp, selDate, selTime, remaining, participants, addItem, t]);

  const bookNow = useCallback(() => {
    if (!selDate || !selTime) { toast.error(t("toast.selectdatetime")); return; }
    addToCart();
    navigate("/vacanza");
  }, [selDate, selTime, addToCart, navigate, t]);

  if (isLoading || !exp) return <Layout><div className="max-w-7xl mx-auto px-4 py-32 text-center text-slate-400">{t("common.loading")}</div></Layout>;

  const subtotal = exp.pricing_type === "per_person" ? exp.price * participants : exp.price;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-28 lg:pb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4"><ChevronLeft className="h-4 w-4" /> {t("common.back")}</button>

        {/* Gallery */}
        <div className="relative rounded-3xl overflow-hidden aspect-[16/10] sm:aspect-[21/9] bg-slate-100" data-testid="experience-gallery">
          <img src={exp.images[imgIdx]} alt={exp.title} className="h-full w-full object-cover animate-fade" />
          {exp.images.length > 1 && (
            <>
              <button onClick={() => setImgIdx((i) => (i - 1 + exp.images.length) % exp.images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full glass flex items-center justify-center"><ChevronLeft className="h-5 w-5 text-slate-800" /></button>
              <button onClick={() => setImgIdx((i) => (i + 1) % exp.images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full glass flex items-center justify-center"><ChevronRight className="h-5 w-5 text-slate-800" /></button>
              <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
                {exp.images.map((im, i) => <button key={`img-dot-${im}-${i}`} onClick={() => setImgIdx(i)} className={`h-2 rounded-full transition-all ${i === imgIdx ? "w-6 bg-white" : "w-2 bg-white/60"}`} />)}
              </div>
            </>
          )}
          {exp.sponsored && <span className="absolute top-4 left-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase text-accent">{t("common.sponsored")}</span>}
        </div>

        <div className="grid lg:grid-cols-3 gap-10 mt-8">
          {/* Main */}
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-3">
              <span className="flex items-center gap-1 font-semibold text-slate-800"><Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /> {exp.rating}</span>
              <span>({exp.reviews_count} {t("exp.reviews").toLowerCase()})</span>
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> Vieste, Gargano</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {Math.round(exp.duration_min / 60 * 10) / 10}h</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900">{exp.title}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("exp.operator")}: <span className="font-semibold text-slate-700">{exp.operator_name}</span></p>

            <div className="flex flex-wrap gap-2 mt-4">
              {exp.tags.map((tag) => <span key={tag} className="rounded-full bg-secondary/10 text-secondary text-xs font-semibold px-3 py-1 capitalize">{tag}</span>)}
            </div>

            <p className="mt-6 text-slate-600 leading-relaxed">{exp.description}</p>

            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-3">{t("exp.included")}</h3>
                <ul className="space-y-2">{exp.included.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {i}</li>)}</ul>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-3">{t("exp.excluded")}</h3>
                <ul className="space-y-2">{exp.excluded.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><X className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" /> {i}</li>)}</ul>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50/50">
                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1"><MapPin className="h-4 w-4 text-primary" /> {t("exp.meeting")}</div>
                <p className="text-sm text-slate-600">{exp.meeting_point}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50/50">
                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1"><Users className="h-4 w-4 text-primary" /> {t("exp.maxparticipants")}</div>
                <p className="text-sm text-slate-600">{exp.max_participants} · {t("exp.minage")} {exp.min_age} {t("exp.years")}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50/50">
                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1"><Shield className="h-4 w-4 text-primary" /> {t("exp.cancellation")}</div>
                <p className="text-sm text-slate-600">{exp.cancellation_policy}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50/50">
                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1"><CloudRain className="h-4 w-4 text-primary" /> {t("exp.weather")}</div>
                <p className="text-sm text-slate-600">{exp.weather_policy}</p>
              </div>
            </div>

            {/* Map */}
            {exp.coordinates?.lat != null && exp.coordinates?.lng != null && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100">
                <iframe title="map" className="w-full h-64" loading="lazy" src={`https://www.google.com/maps?q=${exp.coordinates.lat},${exp.coordinates.lng}&z=13&output=embed`} />
              </div>
            )}

            {/* Reviews */}
            {exp.reviews?.length > 0 && (
              <div className="mt-10">
                <h3 className="text-xl font-heading font-semibold text-slate-900 mb-4">{t("exp.reviews")}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {exp.reviews.slice(0, 6).map((r) => (
                    <div key={r.id} className="rounded-2xl border border-slate-100 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-800">{r.author}</span>
                        <span className="flex items-center gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.id}-star-${i}`} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />)}</span>
                      </div>
                      <p className="text-sm text-slate-600">{r.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-1" id="booking-widget">
            <div className="lg:sticky lg:top-24 scroll-mt-24 rounded-3xl border border-slate-100 shadow-lg p-6 bg-white" data-testid="booking-widget">
              <div className="flex items-baseline gap-2 mb-5">
                <span className="text-sm text-slate-400">{t("exp.from")}</span>
                <span className="text-3xl font-heading font-bold text-primary">{eur(exp.price)}</span>
                <span className="text-sm text-slate-400">{pricingLabel(t, exp.pricing_type)}</span>
              </div>

              <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{t("exp.selectdate")}</label>
              <div className="grid grid-cols-4 gap-1.5 mb-4 max-h-32 overflow-y-auto no-scrollbar" data-testid="date-picker">
                {availability.slice(0, 12).map((a) => (
                  <button key={a.date} data-testid={`date-${a.date}`} onClick={() => { setSelDate(a.date); setSelTime(""); }} className={`rounded-xl px-1 py-2 text-center transition-colors ${selDate === a.date ? "bg-primary text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>
                    <div className="text-[10px] uppercase">{formatDate(a.date, lang).split(" ")[0]}</div>
                    <div className="text-sm font-bold">{a.date.slice(8)}</div>
                  </button>
                ))}
              </div>

              {selDate && (
                <>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{t("exp.selecttime")}</label>
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="time-picker">
                    {daySlots.map((s) => {
                      const full = s.available <= 0;
                      return (
                        <button key={s.time} data-testid={`time-${s.time}`} disabled={full} onClick={() => pickTime(s.time, s.available)} className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${full ? "bg-slate-50 text-slate-300 cursor-not-allowed" : selTime === s.time ? "bg-primary text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}>
                          {s.time} {s.available > 0 && s.available <= 3 && <span className="text-[10px] text-accent">({s.available})</span>}
                        </button>
                      );
                    })}
                  </div>
                  {selTime && (
                    <div data-testid="availability-message" className={`mb-3 text-sm font-semibold ${noSeats ? "text-red-500" : remaining <= 3 ? "text-accent" : "text-emerald-600"}`}>
                      {noSeats ? t("exp.full") : remaining === 1 ? t("exp.lastspot") : `${t("exp.spotsleftpre")} ${remaining} ${t("exp.spotsleftpost")}`}
                    </div>
                  )}
                </>
              )}

              <label className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{t("exp.participants")}</label>
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setParticipants((p) => Math.max(1, p - 1))} className="h-10 w-10 rounded-full border border-slate-200 font-bold text-slate-600 hover:border-primary">−</button>
                <span data-testid="participants-count" className="w-10 text-center font-bold text-lg">{participants}</span>
                <button onClick={() => setParticipants((p) => Math.min(maxSelectable, p + 1))} disabled={remaining != null && participants >= maxSelectable} className="h-10 w-10 rounded-full border border-slate-200 font-bold text-slate-600 hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed">+</button>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-slate-100 mb-4">
                <span className="text-slate-500">{t("cart.total")}</span>
                <span className="text-2xl font-heading font-bold text-primary">{eur(subtotal)}</span>
              </div>

              {settings?.prelaunch_enabled ? (
                <div data-testid="prelaunch-notice" className="w-full rounded-2xl bg-secondary/10 border border-secondary/30 text-center py-4 px-3 text-primary font-semibold">
                  {lang === "it" && settings.prelaunch_text ? settings.prelaunch_text : t("exp.prelaunchdefault")}
                </div>
              ) : (
                <>
                  <button onClick={bookNow} disabled={!selTime || noSeats || overRequested} data-testid="book-now-btn" className="w-full rounded-full bg-primary text-white font-semibold py-3.5 hover:bg-primary/90 transition-colors mb-2 disabled:opacity-40 disabled:cursor-not-allowed">{noSeats ? t("exp.full") : t("exp.book")}</button>
                  <button onClick={addToCart} disabled={!selTime || noSeats || overRequested} data-testid="add-cart-btn" className="w-full rounded-full border border-primary text-primary font-semibold py-3 hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"><Heart className="h-4 w-4" /> {t("exp.addcart")}</button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-6">{t("exp.related")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.slice(0, 4).map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile booking bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-testid="mobile-booking-bar">
        <div className="leading-tight">
          <div className="text-[11px] text-slate-400">{t("exp.from")}</div>
          <div className="text-xl font-heading font-bold text-primary">{eur(exp.price)}</div>
        </div>
        <button
          onClick={() => document.getElementById("booking-widget")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          data-testid="mobile-book-cta"
          className="flex-1 max-w-[200px] rounded-full bg-primary text-white font-semibold py-3 hover:bg-primary/90 transition-colors">
          {t("exp.book")}
        </button>
      </div>
    </Layout>
  );
}
